import { type ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/pt";
import { CalendarIcon, ListIcon, PlusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { RemoteCombobox } from "@/components/ui/remote-combobox";
import { TagsMultiSelect } from "../../components/tags-multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer, PageHeader } from "@/components/layout";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { InspectionSheet } from "../../components/inspections/inspection-sheet";
import type { InspectionRowForSheet } from "../../components/inspections/inspection-sheet";
import InspectionsCalendar from "../../components/inspections-calendar";
import ScheduleInspectionDialog from "../../components/schedule-inspection-dialog";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { Permissions } from "../../entities/permissions";
import {
  useCancelCalendarEventMutation,
  useLazyCalendarControllersQuery,
  useLazyCalendarEventsQuery,
  useScheduleInspectionMutation,
  useUpdateControllerColorMutation,
  useUpdateEventDateMutation,
} from "../../services/api";
import { hasPermission } from "../login/selectors";
import { useQueryState } from "../../utils/query-state";

import {
  useDeleteInspectionMutation,
  useInspectionListQuery,
} from "../../services/api";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface Resource {
  id?: string | number;
  name?: string;
  tags?: TagRef[];
}

interface Flow {
  id?: string | number;
  name?: string;
}

interface InspectionRow {
  id: string | number;
  resource?: Resource;
  flow?: Flow;
  controlDate?: string;
  conformity?: { id?: number };
}

interface InspectionsResponse {
  items?: InspectionRow[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
  error?: unknown;
}

interface AutocompleteValue {
  id?: string | number;
  [key: string]: unknown;
}

interface FilterPayload {
  startDate?: string;
  endDate?: string;
  resourceId?: string;
  tagIds?: number[];
  flowId?: string;
  controllerId?: string;
}

interface Controller {
  id: number | string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  visible?: boolean;
}

type ViewMode = "list" | "calendar";

interface CalendarEvent {
  controlId?: number | string;
  [key: string]: unknown;
}

function conformityClass(id: number | undefined) {
  if (id === 1) return "text-emerald-600 dark:text-emerald-400";
  if (id === 2) return "text-sky-600 dark:text-sky-400";
  return "text-destructive";
}

function conformityLabel(
  id: number | undefined,
  t: (k: string) => string
): string {
  if (id === 1) return t("inspections.items.yes");
  if (id === 2) return t("inspections.items.scheduled");
  return t("inspections.items.no");
}

export default function Inspections() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();

  const hasRemovePermission = useSelector(
    hasPermission(Permissions.PAGE_INSPECTIONS_REMOVE)
  );
  const canCreateControl = useSelector(
    hasPermission(Permissions.PAGE_DASHBOARD_CALENDAR_CREATE_CONTROL)
  );

  // View mode persisted in the URL so links are shareable / back-button works.
  const [view, setView] = useQueryState({
    paramName: "view",
    fromQuery: (v: string | null) => (v === "calendar" ? "calendar" : "list"),
    toQuery: (v: ViewMode) => v,
    defaultValue: "list" as ViewMode,
  }) as [ViewMode, (next: ViewMode) => void];

  // Detail sheet open state, persisted in URL alongside `view`.
  const [selectedDetailId, setSelectedDetailId] = useQueryState({
    paramName: "detail",
    fromQuery: (v: string | null) => v || null,
    toQuery: (v: string | null) => (v ? String(v) : ""),
    defaultValue: null,
  }) as [string | null, (next: string | null) => void];

  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<FilterPayload | null>(null);

  const [openDeleteInspectionDialog, setOpenDeleteInspectionDialog] =
    useState(false);
  const [selectedInspection, setSelectedInspection] =
    useState<InspectionRow | null>(null);

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  // Controllers fetched once on mount so both the FiltersCard select and the
  // ScheduleInspectionDialog can reuse the same list. The calendar component
  // has its own internal fetch via fetchControllersFunction.
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const calendarRefreshRef = useRef<(() => void) | null>(null);

  const {
    data: inspections,
    isFetching: fetchingList,
  } = useInspectionListQuery({
    includeReportGenerating: true,
    range: { page: page + 1 },
    filters: filters ?? undefined,
  }) as {
    data?: InspectionsResponse;
    isFetching: boolean;
  };
  const [deleteInspection, { isLoading: isDeleting }] =
    useDeleteInspectionMutation();
  const [triggerCalendarEvents] = useLazyCalendarEventsQuery();
  const [triggerCalendarControllers] = useLazyCalendarControllersQuery();
  const [updateControllerColor] = useUpdateControllerColorMutation();
  const [cancelCalendarEvent] = useCancelCalendarEventMutation();
  const [updateEventDate] = useUpdateEventDateMutation();
  const [scheduleInspection] = useScheduleInspectionMutation();

  // Load controllers once for both the filter dropdown and the schedule dialog.
  useEffect(() => {
    setLoadingControllers(true);
    triggerCalendarControllers(undefined)
      .unwrap()
      .then((result) => {
        const r = result as { controllers?: Controller[] };
        setControllers(r?.controllers ?? []);
      })
      .catch(() => undefined)
      .finally(() => setLoadingControllers(false));
  }, [triggerCalendarControllers]);

  // When the active filters change in calendar mode, ask the calendar to
  // re-fetch with the new params.
  useEffect(() => {
    if (view === "calendar" && calendarRefreshRef.current) {
      calendarRefreshRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, view]);

  // Pulls the FiltersCard payload into the calendar's per-range fetch so both
  // views stay consistent. The calendar passes its visible range first; we
  // override / extend with the unified filters.
  const fetchCalendarEventsWithFilters = useCallback(
    (params: Record<string, unknown>) => {
      const merged: Record<string, unknown> = { ...params };
      if (filters?.resourceId) merged.resourceId = filters.resourceId;
      if (filters?.tagIds && filters.tagIds.length > 0) merged.tagIds = filters.tagIds;
      if (filters?.flowId) merged.flowId = filters.flowId;
      if (filters?.controllerId) {
        merged.controllerIds = [filters.controllerId];
      }
      return triggerCalendarEvents(merged).unwrap();
    },
    [triggerCalendarEvents, filters]
  );

  function onSee(row: InspectionRow) {
    // Row click opens the right-anchored summary sheet; from the sheet
    // the user can expand to the full /dashboard/inspections/:id page.
    setSelectedInspection(row);
    setSelectedDetailId(String(row.id));
  }

  function onExpandToFullView() {
    if (!selectedDetailId) return;
    navigate(`/dashboard/inspections/${selectedDetailId}`, {
      state: { inspection: selectedInspection },
    });
  }

  function onDelete(row: InspectionRow) {
    setSelectedInspection(row);
    setOpenDeleteInspectionDialog(true);
  }

  async function onDeleted() {
    if (!selectedInspection?.id) {
      showNotification("error", t("common.api-error"));
      setOpenDeleteInspectionDialog(false);
      return;
    }
    try {
      await deleteInspection(selectedInspection.id).unwrap();
      showNotification(
        "success",
        t("inspections.delete-inspection-dialog.success", {
          resource: selectedInspection.resource?.name,
          flow: selectedInspection.flow?.name,
          date: selectedInspection.controlDate,
        }),
      );
      // Close the sheet if it was open on the deleted row.
      if (selectedDetailId === String(selectedInspection.id)) {
        setSelectedDetailId(null);
      }
    } catch {
      showNotification("error", t("common.api-error"));
      // Re-throw so ConfirmDialog keeps itself open for retry.
      throw new Error("delete-failed");
    }
  }

  function onSearch(next: FilterPayload) {
    setFilters(next);
    setPage(0);
  }

  function onPageChange(next: number) {
    setPage(next);
  }

  function handleOpenScheduleDialog() {
    setScheduleDialogOpen(true);
  }

  const handleScheduleInspection = async (data: unknown) =>
    scheduleInspection(data).unwrap();

  function handleInspectionScheduled() {
    showNotification("success", t("schedule-inspection-dialog.success"));
    if (view === "calendar" && calendarRefreshRef.current) {
      calendarRefreshRef.current();
    }
    // List view: the scheduleInspection mutation invalidates the
    // Inspection LIST tag, so RTK Query refetches the list automatically.
  }

  const columns = useMemo<ColumnDef<InspectionRow>[]>(() => {
    const cols: ColumnDef<InspectionRow>[] = [
      {
        id: "resourceName",
        header: t("inspections.items.resource"),
        cell: ({ row }) => row.original.resource?.name ?? "",
      },
      {
        id: "tags",
        header: t("inspections.items.tags"),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {(row.original.resource?.tags ?? []).map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.color ?? undefined,
                  color: tag.color ? "#fff" : undefined,
                }}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: "flowName",
        header: t("inspections.items.flow-type"),
        cell: ({ row }) => row.original.flow?.name ?? "",
      },
      {
        accessorKey: "controlDate",
        header: t("inspections.items.date"),
      },
      {
        id: "conformity",
        header: t("inspections.items.appropriate"),
        cell: ({ row }) => {
          const conformityId = row.original.conformity?.id;
          if (conformityId == null) return null;
          return (
            <span className={cn(conformityClass(conformityId))}>
              {conformityLabel(conformityId, t)}
            </span>
          );
        },
      },
    ];
    // Delete affordance lives in the sheet header now — row click opens
    // the InspectionSheet, which carries the destructive trash button.
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const rows = inspections?.items ?? [];
  const pageSize = inspections?.range?.limit ?? 15;
  const pageCount = inspections?.itemCount
    ? Math.max(1, Math.ceil(inspections.itemCount / pageSize))
    : 1;

  // Resolve the sheet's backing row from the list cache.
  const selectedSheetInspection = useMemo(() => {
    if (!selectedDetailId) return null;
    return (
      rows.find((row) => String(row.id) === selectedDetailId) ??
      selectedInspection ??
      null
    );
  }, [selectedDetailId, rows, selectedInspection]);

  return (
    <>
      <ConfirmDialog
        open={openDeleteInspectionDialog}
        onOpenChange={(next) => { if (!next) setOpenDeleteInspectionDialog(false); }}
        title={t("inspections.delete-inspection-dialog.title", {
          resource: selectedInspection?.resource?.name,
          flow: selectedInspection?.flow?.name,
          date: selectedInspection?.controlDate,
        })}
        confirmLabel={t("common.delete")}
        onConfirm={onDeleted}
        isPending={isDeleting}
      />
      <InspectionSheet
        value={selectedSheetInspection as InspectionRowForSheet | null}
        open={selectedDetailId != null}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedDetailId(null);
            setSelectedInspection(null);
          }
        }}
        onExpand={onExpandToFullView}
        onDelete={
          hasRemovePermission && selectedSheetInspection
            ? () => onDelete(selectedSheetInspection)
            : undefined
        }
      />
      <ScheduleInspectionDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onScheduled={handleInspectionScheduled}
        // PropTypes-only typing on this still-MUI dialog widens to never[]
        // via the default-arg pattern; cast at the call site.
        controllers={controllers as never[]}
        loadingControllers={loadingControllers}
        scheduleInspectionFunction={handleScheduleInspection}
      />
      <PageContainer title={t("inspections.tab-title")}>
        <PageHeader
          title={t("inspections.page-title")}
          description={t("inspections.info-message")}
          actions={
            canCreateControl ? (
              <Button onClick={handleOpenScheduleDialog}>
                <PlusIcon className="size-4" />
                {t("inspections.schedule-button")}
              </Button>
            ) : null
          }
        />

        <div className="mt-6">
          <InspectionsFiltersCard
            onSearch={onSearch}
            controllers={controllers}
            loadingControllers={loadingControllers}
          />
        </div>

        <div className="mt-6 flex justify-end">
          <Tabs
            value={view}
            onValueChange={(next) => setView(next as ViewMode)}
          >
            <TabsList>
              <TabsTrigger value="list">
                <ListIcon className="size-4" />
                {t("inspections.view.list", "List")}
              </TabsTrigger>
              <TabsTrigger value="calendar">
                <CalendarIcon className="size-4" />
                {t("inspections.view.calendar", "Calendar")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {view === "list" ? (
          <div className="mt-4">
            <DataTable
              columns={columns}
              data={rows}
              emptyMessage={t("common.no-data", "No data")}
              onRowClick={onSee}
              loading={fetchingList}
              skeletonRows={pageSize}
              manualPagination={{
                pageIndex: page,
                pageCount,
                onPageChange,
              }}
            />
          </div>
        ) : (
          <div className="mt-4">
            <InspectionsCalendar
              fetchEventsFunction={fetchCalendarEventsWithFilters}
              fetchControllersFunction={() =>
                triggerCalendarControllers(undefined).unwrap()
              }
              updateControllerColorFunction={(userId: number, color: string) =>
                updateControllerColor({ userId, color }).unwrap()
              }
              cancelEventFunction={(type: string, controlId: number) =>
                cancelCalendarEvent({ type, controlId }).unwrap()
              }
              updateEventDateFunction={(
                _type: string,
                controlId: number,
                newDate: string
              ) => updateEventDate({ controlId, newDate }).unwrap()}
              onRefreshRef={(fn: () => void) => {
                calendarRefreshRef.current = fn;
              }}
              onControlClick={(event: CalendarEvent) => {
                if (event.controlId) {
                  setSelectedDetailId(String(event.controlId));
                }
              }}
              onCreateControl={() => handleOpenScheduleDialog()}
              canCreateControl={canCreateControl}
              canRescheduleEvents={canCreateControl}
              canCancelOverdueEvents={canCreateControl}
              // FiltersCard already exposes a controller filter; hide the
              // calendar's internal one so the FiltersCard is the single
              // source of truth.
              showControllerFilter={false}
              height="700px"
            />
          </div>
        )}
      </PageContainer>
    </>
  );
}

// ---- Filters card ------------------------------------------------------

interface InspectionsFiltersCardProps {
  onSearch: (filters: FilterPayload) => void;
  controllers: Controller[];
  loadingControllers: boolean;
}

function controllerLabel(c: Controller): string {
  if (c.firstName || c.lastName) {
    return `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
  }
  return c.name ?? c.email ?? String(c.id);
}

function InspectionsFiltersCard({
  onSearch,
  controllers,
  loadingControllers,
}: InspectionsFiltersCardProps) {
  const { t } = useTranslation();

  const [resource, setResource] = useState<AutocompleteValue | null>(null);
  const [filterTags, setFilterTags] = useState<TagRef[]>([]);
  const [flow, setFlow] = useState<string>("");
  const [controllerId, setControllerId] = useState<string>("");
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);

  // Browser locale for the DatePickers' visible format.
  const locale = (
    typeof navigator !== "undefined"
      ? navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage || "en"
      : "en"
  ).substring(0, 2);
  const dateFormat = locale === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY";

  function onCancel() {
    setResource(null);
    setFilterTags([]);
    setFlow("");
    setControllerId("");
    setStartDate(null);
    setEndDate(null);
    onSearch({});
  }

  function handleSearch() {
    const payload: FilterPayload = {};
    if (startDate?.isValid()) payload.startDate = startDate.format();
    if (endDate?.isValid()) payload.endDate = endDate.format();
    if (resource?.id != null) payload.resourceId = String(resource.id);
    if (filterTags.length > 0) payload.tagIds = filterTags.map((tag) => tag.id);
    if (flow) payload.flowId = flow;
    if (controllerId) payload.controllerId = controllerId;
    onSearch(payload);
  }

  return (
    <Card>
      <CardContent className="space-y-5">
        <h3 className="text-base font-semibold">
          {t("inspections.filter.title")}
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <RemoteCombobox
            resource="resource/name-autocomplete"
            value={resource}
            onChange={setResource}
            label={t("inspections.filter.resource")}
          />
          <TagsMultiSelect
            value={filterTags}
            onChange={setFilterTags}
            label={t("inspections.filter.tags")}
            placeholder={t("tags.multi-select.placeholder")}
            mode="filter"
          />
          <div className="space-y-1">
            <label htmlFor="flow-type" className="text-sm font-medium">
              {t("flows.add-flow-dialog.type")}
            </label>
            <Select value={flow || undefined} onValueChange={setFlow}>
              <SelectTrigger id="flow-type" className="w-full">
                <SelectValue placeholder={t("flows.add-flow-dialog.type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">
                  {t("flows.add-flow-dialog.select-type.free")}
                </SelectItem>
                <SelectItem value="1">
                  {t("flows.add-flow-dialog.select-type.sequencial")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label htmlFor="controller-filter" className="text-sm font-medium">
              {t("inspections.filter.controller", "Controller")}
            </label>
            <Select
              value={controllerId || undefined}
              onValueChange={setControllerId}
              disabled={loadingControllers}
            >
              <SelectTrigger id="controller-filter" className="w-full">
                <SelectValue
                  placeholder={t(
                    "inspections.filter.controller",
                    "Controller"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {controllers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {controllerLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DatePicker
            label={t("inspections.filter.start-date")}
            value={startDate}
            onChange={setStartDate}
            format={dateFormat}
            locale={locale}
            placeholder={t("inspections.filter.start-date")}
          />
          <DatePicker
            label={t("inspections.filter.end-date")}
            value={endDate}
            onChange={setEndDate}
            format={dateFormat}
            locale={locale}
            placeholder={t("inspections.filter.end-date")}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>
            {t("common.clear", "Clear")}
          </Button>
          <Button onClick={handleSearch}>
            {t("components.table.toolbar.search")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
